import { t } from 'i18next';
import { useCallback } from 'react';
import { useMutation, useQuery } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { tableService } from 'core/services';
import { GetManyResult, IActionOptions, ICustomFilter, IRequestPayload } from 'core/types';

export const useTableActions = ({ notificationPosition, silent }: IActionOptions = {}) => ({
  ...useUpsertCustomLayout(notificationPosition),
  ...useRemoveCustomLayout(notificationPosition),
  ...useUpsertCustomFilter(notificationPosition, silent),
  ...useRemoveCustomFilter(notificationPosition),
});

const useUpsertCustomLayout = (notificationPosition = NotificationPosition.TOP) => {
  const { errors } = useHandleErrors({
    success: (data) =>
      data.id
        ? { message: t('messageCustomTableLayoutUpdateSuccess') }
        : { message: t('messageCustomTableLayoutCreateSuccess') },
    loading: (data) =>
      data.id
        ? { message: t('messageCustomTableLayoutUpdateInProgress') }
        : { message: t('messageCustomTableLayoutCreateInProgress') },
    position: notificationPosition,
    invalidateQueries: ['authUser'],
  });

  const { mutateAsync } = useMutation((data) => tableService.upsertCustomLayout(data), {
    ...errors,
  });

  const upsertCustomLayout = (data) => mutateAsync(data);

  return { upsertCustomLayout };
};

const useRemoveCustomLayout = (notificationPosition = NotificationPosition.TOP) => {
  const { errors } = useHandleErrors({
    success: { message: t('messageCustomTableLayoutRemoveSuccess') },
    loading: { message: t('messageCustomTableLayoutRemoveInProgress') },
    position: notificationPosition,
    invalidateQueries: ['authUser'],
  });

  const { mutateAsync } = useMutation((data) => tableService.removeCustomLayout(data), {
    ...errors,
  });

  const removeCustomLayout = (data) => mutateAsync(data);

  return { removeCustomLayout };
};

export const useGetCustomFilters = (
  requestPayload: IRequestPayload,
  options: {
    enabled?: boolean;
  } = {}
): GetManyResult<ICustomFilter> => {
  const { data, isLoading, isError } = useQuery(
    ['customFilters', requestPayload],
    ({ signal }) => tableService.getCustomFilters(requestPayload, signal),
    {
      keepPreviousData: false,
      ...options,
    }
  );

  return {
    records: data?.records ?? null,
    count: data?.count ?? 0,
    isLoading,
    isError,
  };
};

const useUpsertCustomFilter = (notificationPosition = NotificationPosition.TOP, silent = false) => {
  const { errors } = useHandleErrors({
    ...(silent
      ? {}
      : {
          success: (data) =>
            data.id
              ? { message: t('messageCustomTableFilterUpdateSuccess') }
              : { message: t('messageCustomTableFilterCreateSuccess') },
          loading: (data) =>
            data.id
              ? { message: t('messageCustomTableFilterUpdateInProgress') }
              : { message: t('messageCustomTableFilterCreateInProgress') },
        }),
    position: notificationPosition,
    invalidateQueries: ['authUser', 'customFilters'],
  });

  const { mutateAsync } = useMutation((data) => tableService.upsertCustomFilter(data), {
    ...errors,
  });

  const upsertCustomFilter = useCallback((data) => mutateAsync(data), [mutateAsync]);

  return { upsertCustomFilter };
};

const useRemoveCustomFilter = (notificationPosition = NotificationPosition.TOP) => {
  const { errors } = useHandleErrors({
    success: { message: t('messageCustomTableFilterRemoveSuccess') },
    loading: { message: t('messageCustomTableFilterRemoveInProgress') },
    position: notificationPosition,
    invalidateQueries: ['authUser', 'customFilters'],
  });

  const { mutateAsync } = useMutation((data) => tableService.removeCustomFilter(data), {
    ...errors,
  });

  const removeCustomFilter = (data) => mutateAsync(data);

  return { removeCustomFilter };
};
